import React from 'react';
import PixelIcons from '../components/PixelIcons.jsx';
import { useT } from '../i18n/useT.js';

// Portfolio.jsx — Project cartridges with category filters
const Portfolio = (() => {
  const { Arrow, Joystick, Diamond, Star, Mushroom } = PixelIcons;

  const allProjects = [
    { code: "#01", img: "/assets/portfolio/pacific-travel-videojuego.jpg", cat: "videojuego",  stack: "UNITY · WebGL",       name: "Pacific Travel", k: "p1", tag: "FEATURED",   tagColor: "var(--ragross-gold)"    },
    { code: "#02", img: "/assets/portfolio/abcespanol-videojuego.jpg",     cat: "videojuego",  stack: "EDUCATIVO",           name: "Abcespañol",     k: "p2", tag: "PLAY NOW",   tagColor: "var(--ragross-green)"   },
    { code: "#03", img: "/assets/portfolio/ymca-home.png",                 cat: "web",         stack: "WEB · INSTITUCIONAL", name: "YMCA Cali",      k: "p3", tag: "LIVE",       tagColor: "var(--ragross-cyan)"    },
    { code: "#04", img: "/assets/portfolio/districya-home.jpg",            cat: "web",         stack: "REACT · BRAND",       name: "Districya",      k: "p4", tag: "LIVE",       tagColor: "var(--ragross-cyan)"    },
    { code: "#05", img: "/assets/portfolio/tupitube-landing.jpg",          cat: "web",         stack: "LANDING",             name: "Tupitube",       k: "p5", tag: "ARCHIVED",   tagColor: "var(--fg-3)"            },
    { code: "#06", img: "/assets/portfolio/natamar.jpg",                   cat: "audiovisual", stack: "FOTO · PRODUCCIÓN",   name: "NataMar",        k: "p6", tag: "PROD",       tagColor: "var(--ragross-magenta)" },
    { code: "#07", img: "/assets/portfolio/daater-landing.jpg",            cat: "web",         stack: "REACT · APP",         name: "Daater",         k: "p7", tag: "LIVE",       tagColor: "var(--ragross-cyan)"    },
    { code: "#08", img: "/assets/portfolio/maefloresta.png",               cat: "multimedia",  stack: "AR · EDUCATIVO",      name: "Maefloresta",    k: "p8", tag: "PLAY NOW",   tagColor: "var(--ragross-green)"   },
  ];

  const pfStyles = {
    section: {
      padding: "112px 0",
      background:
        "linear-gradient(180deg, transparent 0%, rgba(57,34,110,0.12) 100%), var(--bg-app)",
      borderTop: "1px solid var(--border-soft)",
      borderBottom: "1px solid var(--border-soft)",
    },
    container: { maxWidth: 1280, margin: "0 auto", padding: "0 32px" },
    head: { display: "flex", flexDirection: "column", gap: 14, maxWidth: 720, marginBottom: 36 },
    tag: {
      display: "inline-flex", alignItems: "center", gap: 12,
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "11px", letterSpacing: "0.16em",
      color: "var(--ragross-magenta)",
    },
    title: { font: "var(--type-display-lg)", letterSpacing: "-0.015em", lineHeight: 1.05, color: "#fff", margin: 0 },
    lead: { font: "var(--type-body-lg)", color: "var(--fg-2)", maxWidth: 620, margin: 0 },
    filters: {
      display: "flex", flexWrap: "wrap", gap: 10,
      marginBottom: 36,
      padding: "12px",
      background: "var(--bg-surface)",
      border: "1px solid var(--border-soft)",
      borderRadius: "var(--radius-card)",
    },
    grid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 18 },
    cartridge: {
      background: "var(--ragross-purple-deep)",
      border: "2px solid var(--ragross-magenta)",
      borderRadius: "var(--radius-card)",
      overflow: "hidden",
      cursor: "pointer",
      transition: "transform 200ms var(--ease-arcade), box-shadow 200ms",
      boxShadow: "4px 4px 0 0 var(--ragross-cyan)",
      display: "flex",
      flexDirection: "column",
    },
    screen: {
      position: "relative",
      aspectRatio: "16 / 10",
      background: "var(--bg-screen)",
      overflow: "hidden",
    },
    screenImg: {
      width: "100%", height: "100%", objectFit: "cover",
      filter: "saturate(0.92) contrast(1.02)",
    },
    screenScan: {
      position: "absolute", inset: 0,
      background: "var(--scanlines)",
      pointerEvents: "none",
      opacity: 0.5,
    },
    screenTag: {
      position: "absolute", top: 8, left: 8,
      padding: "4px 7px",
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "8px", letterSpacing: "0.10em",
      background: "rgba(11,0,20,0.85)",
      borderRadius: 2,
    },
    screenCode: {
      position: "absolute", top: 8, right: 8,
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "9px", letterSpacing: "0.10em",
      color: "var(--ragross-gold)",
    },
    /* Cartridge "label" strip — like a NES cartridge label */
    label: {
      padding: "6px 10px",
      background: "linear-gradient(90deg, var(--ragross-magenta), var(--ragross-purple-2))",
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 8,
      letterSpacing: "0.12em",
      color: "#fff",
      textAlign: "center",
      borderTop: "1px solid rgba(255,255,255,0.15)",
      borderBottom: "1px solid rgba(0,0,0,0.4)",
    },
    meta: { padding: 14, display: "flex", flexDirection: "column", gap: 6 },
    stack: {
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "8px", letterSpacing: "0.10em",
      color: "var(--ragross-cyan)",
    },
    name: { font: "var(--type-h3)", color: "#fff", margin: 0 },
    role: { font: "var(--type-caption)", color: "var(--fg-3)", margin: 0 },
  };

  function FilterButton({ f, active, onClick, t }) {
    const Icon = f.icon;
    return (
      <button
        onClick={onClick}
        className="btn"
        style={{
          background: active ? "var(--ragross-magenta)" : "transparent",
          color: active ? "#fff" : "var(--fg-2)",
          borderColor: active ? "var(--ragross-magenta)" : "var(--border-soft)",
          boxShadow: active ? "var(--shadow-pixel-cy)" : "none",
          padding: "10px 14px",
          fontSize: "10px",
        }}
      >
        <Icon size={11}/>
        <span>{t(f.labelK)}</span>
      </button>
    );
  }

  function Cartridge({ p, t }) {
    const [hover, setHover] = React.useState(false);
    return (
      <article
        style={{
          ...pfStyles.cartridge,
          transform: hover ? "translate(-2px, -2px)" : "none",
          boxShadow: hover
            ? "6px 6px 0 0 var(--ragross-cyan), 0 0 40px rgba(253,20,145,0.4)"
            : pfStyles.cartridge.boxShadow,
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div style={pfStyles.screen}>
          <img src={p.img} alt={p.name} style={pfStyles.screenImg}/>
          <div style={pfStyles.screenScan}></div>
          <span style={{ ...pfStyles.screenTag, color: p.tagColor, border: `1px solid ${p.tagColor}` }}>
            ▸ {p.tag}
          </span>
          <span style={pfStyles.screenCode}>{p.code}</span>
        </div>
        <div style={pfStyles.label}>RAGROSS · {p.code} · {p.cat.toUpperCase()}</div>
        <div style={pfStyles.meta}>
          <span style={pfStyles.stack}>{p.stack}</span>
          <h3 style={pfStyles.name}>{p.name}</h3>
          <p style={pfStyles.role}>{t(`pf.${p.k}.role`)}</p>
        </div>
      </article>
    );
  }

  function Portfolio() {
    const t = useT();
    const [filter, setFilter] = React.useState("all");
    const visible = filter === "all"
      ? allProjects
      : allProjects.filter((p) => p.cat === filter);

    const filters = [
      { id: "all",         labelK: "pf.filterAll",  icon: Diamond },
      { id: "videojuego",  labelK: "pf.filterGame", icon: Joystick },
      { id: "web",         labelK: "pf.filterWeb",  icon: Star },
      { id: "audiovisual", labelK: "pf.filterAv",   icon: Mushroom },
      { id: "multimedia",  labelK: "pf.filterMm",   icon: Diamond },
    ];

    return (
      <section
        id="portfolio"
        style={pfStyles.section}
        data-screen-label="04 Portfolio"
      >
        <div style={pfStyles.container}>
          <div style={pfStyles.head}>
            <div style={pfStyles.tag}>
              <span>{t("pf.tag")}</span>
              <span style={{ width: 60, height: 1, background: "linear-gradient(90deg, var(--ragross-magenta), transparent)" }}></span>
            </div>
            <h2 style={pfStyles.title} className="section-title">
              {t("pf.title1")} <span style={{ color: "var(--ragross-cyan)" }}>{t("pf.title2")}</span>
            </h2>
            <p style={pfStyles.lead}>{t("pf.lead")}</p>
          </div>

          <div style={pfStyles.filters}>
            {filters.map((f) => (
              <FilterButton
                key={f.id}
                f={f}
                active={filter === f.id}
                onClick={() => setFilter(f.id)}
                t={t}
              />
            ))}
          </div>

          <div style={pfStyles.grid} className="responsive-grid-4">
            {visible.map((p) => <Cartridge key={p.code} p={p} t={t}/>)}
          </div>

          <div style={{ marginTop: 32, textAlign: "center" }}>
            <button className="btn btn-secondary">
              <span>{t("pf.more")}</span>
              <Arrow size={11} color="var(--ragross-cyan)"/>
            </button>
          </div>
        </div>
      </section>
    );
  }

  return Portfolio;
})();

export default Portfolio;
